import { apiRequest } from 'platform/utilities/api';
import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import { countries } from 'platform/forms/address';

/**
 * Converts a form address object to the snake_case format
 * required by the /v0/profile/address_validation endpoint
 * avoiding eslint issues.
 *
 * @param {Object} address - Form address object
 * @returns {Object} API-formatted address
 */
const ADDRESS_LINE1 = 'address_line1';
const ADDRESS_LINE2 = 'address_line2';
const ADDRESS_POU = 'address_pou';
const ADDRESS_TYPE = 'address_type';
const COUNTRY_CODE_ISO3 = 'country_code_iso3';
const STATE_CODE = 'state_code';
const PROVINCE = 'province';
const ZIP_CODE = 'zip_code';
const INT_POSTAL_CODE = 'int_postal_code';
const MIN_CONFIDENCE_SCORE_FOR_SUGGESTION = 50;

export const prepareAddressForAPI = address => {
  const countryCode = address.country || 'USA';

  return {
    [ADDRESS_LINE1]: address.street,
    [ADDRESS_LINE2]: address.street2 || undefined,
    [ADDRESS_POU]: 'CORRESPONDENCE',
    [ADDRESS_TYPE]: countryCode === 'USA' ? 'DOMESTIC' : 'INTERNATIONAL',
    city: address.city,
    [COUNTRY_CODE_ISO3]: countryCode,
    [countryCode === 'USA' ? STATE_CODE : PROVINCE]: address.state,
    [countryCode === 'USA' ? ZIP_CODE : INT_POSTAL_CODE]: address.postalCode,
  };
};

/**
 * Calls the address validation endpoint and returns the
 * suggested address along with a flag indicating whether
 * suggestions should be shown to the user.
 *
 * @param {Object} userAddress - Form address object
 * @returns {Promise<{suggestedAddress: Object|null, showSuggestions: boolean}>}
 */
export const fetchSuggestedAddress = async userAddress => {
  const options = {
    body: JSON.stringify({
      address: { ...prepareAddressForAPI(userAddress) },
    }),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  };

  try {
    const res = await apiRequest(
      `${environment.API_URL}/v0/profile/address_validation`,
      options,
    );

    if (res?.addresses?.length > 0) {
      const firstAddress = res.addresses[0] || {};
      const suggested = firstAddress.address;
      const { confidenceScore, deliveryPointValidation } =
        firstAddress.addressMetaData || {};

      if (!suggested) {
        // Some upstream responses can include metadata without a usable
        // normalized address payload. Treat this as "no suggestion" so the UI
        // falls back to confirmation instead of crashing.
        return {
          suggestedAddress: null,
          confidenceScore,
          showSuggestions: false,
          deliveryPointValidation,
        };
      }

      const hasSuggestionConfidence =
        typeof confidenceScore === 'number' &&
        confidenceScore >= MIN_CONFIDENCE_SCORE_FOR_SUGGESTION &&
        confidenceScore < 100;

      return {
        suggestedAddress: {
          street: suggested.addressLine1,
          street2: suggested.addressLine2,
          city: suggested.city,
          country: suggested.countryCodeIso3,
          state: suggested.stateCode,
          postalCode: suggested.zipCode,
        },
        confidenceScore,
        // Confidence below the minimum threshold (e.g., 0 with MISSING_ZIP)
        // is treated as non-actionable and should not appear as a suggestion.
        showSuggestions: hasSuggestionConfidence,
        deliveryPointValidation,
      };
    }
  } catch {
    // If USPS is unavailable, do not block form progress.
    return { suggestedAddress: null, showSuggestions: false };
  }

  return { suggestedAddress: null, showSuggestions: false };
};

/**
 * Formats an address object into a single display string.
 *
 * @param {Object} address - Address object (supports both form and API field names)
 * @returns {string} Formatted address string
 */
export const formatAddress = address => {
  if (!address) return '';

  const street = address.street || address.addressLine1;
  const street2 = address.street2 || address.addressLine2;
  const { city } = address;
  const state = address.state || address.stateCode;
  const zip = address.postalCode || address.zipCode;
  const country = address.country || address.countryCodeIso3;

  let display = '';
  if (street) display += street;
  if (street2) display += `, ${street2}`;
  if (city) display += `, ${city}`;
  if (state) display += `, ${state}`;
  if (zip) display += ` ${zip}`;
  if (country && country !== 'USA') {
    const label = countries.find(c => c.value === country)?.label || country;
    display += `, ${label}`;
  }

  return display.trim();
};
